import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import theme, { mobileBreakpoint } from '../theme';

// Icons for different course types
const ArtIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3ZM12 19C8.13 19 5 15.87 5 12C5 8.13 8.13 5 12 5C15.87 5 19 8.13 19 12C19 15.87 15.87 19 12 19Z" fill="black"/>
    <path d="M15 9L12 15L9 9H15Z" fill="black"/>
  </svg>
);

const WritingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.06 9.02L14.98 9.94L5.92 19H5V18.08L14.06 9.02ZM17.66 3C17.41 3 17.15 3.1 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C18.17 3.09 17.92 3 17.66 3ZM14.06 6.19L3 17.25V21H6.75L17.81 9.94L14.06 6.19Z" fill="black"/>
  </svg>
);

const SculptingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3ZM15.04 16.5H8.96C8.39 15.31 8 13.28 8 11C8 8.72 8.39 6.69 8.96 5.5H15.04C15.61 6.69 16 8.72 16 11C16 13.28 15.61 15.31 15.04 16.5Z" fill="black"/>
  </svg>
);

const TagContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  font-size: 12px;
  margin-bottom: 12px;
  position: relative;
  z-index: 1002;
`;

const CardContainer = styled(motion.div)`
  background-color: ${props => props.bgColor || '#4DDA61'};
  border-radius: 20px;
  padding: 24px;
  color: ${props => props.textColor || 'black'};
  display: flex;
  flex-direction: ${props => props.layout === 'horizontal' ? 'row' : 'column'};
  justify-content: space-between;
  height: 100%;
  min-height: 250px;
  cursor: pointer;
  overflow: visible;
  position: relative;
  word-wrap: break-word;
  box-sizing: border-box;
  z-index: 1000;

  @media (max-width: 890px) and (min-width: 769px) {
    padding: 24px;
    flex-direction: column;
    justify-content: flex-start;
  }

  @media (max-width: ${mobileBreakpoint}px) {
    padding: 20px;
    min-height: unset;
    height: auto;
    justify-content: space-between;
    flex-direction: row;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${props => props.isMain ? 'center' : 'flex-start'};
  flex: ${props => props.isMain ? '2' : '1'};
  z-index: 1001;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
  position: relative;

  @media (max-width: 890px) and (min-width: 769px) {
    padding-right: 0;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding-right: 15px;
  }
`;

const Title = styled.h3`
  font-size: ${props => props.isMain ? '36px' : '24px'};
  font-weight: bold;
  margin-bottom: 8px;
  margin-top: 0;
  word-wrap: break-word;
`;

const Subtitle = styled.h4`
  font-size: ${props => props.isMain ? '36px' : '20px'};
  font-style: italic;
  margin-top: 0;
  margin-bottom: 16px;
  word-wrap: break-word;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.5;
  max-width: ${props => props.isMain ? '400px' : '100%'};
  overflow-wrap: break-word;
  word-break: break-word;
  flex-grow: 1;

  @media (max-width: 890px) and (min-width: 769px) {
    margin-bottom: 0;
  }

  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 15px;
    line-height: 1.4;
    margin-bottom: 15px;
    margin-top: 8px;
  }
`;

const ImageContainer = styled.div`
  border-radius: 50%;
  overflow: hidden;
  width: ${props => props.size || '100px'};
  height: ${props => props.size || '100px'};
  margin-left: ${props => props.layout === 'horizontal' ? '20px' : '0'};
  margin-top: auto;
  margin-bottom: 10px;
  align-self: ${props => props.layout === 'horizontal' ? 'center' : 'flex-end'};
  flex-shrink: 0;
  background-color: #FFF8E1;
  position: relative;
  right: 0;
  bottom: ${props => props.moveUpPixels ? `${props.moveUpPixels}px` : '0'};
  z-index: 1001;

  @media (max-width: 990px) and (min-width: 769px) {
    display: none;
  }

  @media (max-width: ${mobileBreakpoint}px) {
    width: 80px;
    height: 80px;
    margin-left: 15px;
    align-self: flex-start;
    margin-top: 25px;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const ArrowButton = styled.button`
  background-color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 16px;
  align-self: flex-end;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1002;
`;

const TitleSubtitleRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ContentRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const getIcon = (type) => {
  switch (type) {
    case 'writing':
      return <WritingIcon />;
    case 'sculpting':
      return <SculptingIcon />;
    case 'art':
    default:
      return <ArtIcon />;
  }
};

const CourseCard = ({ 
  title,
  subtitle,
  description,
  ageGroup,
  type = 'art',
  color = '#4DDA61',
  textColor = 'black',
  layout = 'vertical',
  isMain = false,
  hasButton = true,
  hasImage = true,
  imagePosition = 'center',
  moveUp = false,
  moveUpPixels = 0,
  image
}) => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  const effectiveLayout = isMobile ? 'horizontal' : layout;
  
  return (
    <CardContainer 
      bgColor={color}
      textColor={textColor}
      layout={effectiveLayout}
      whileHover={{ scale: 1.03 }}
      transition={{ duration: 0.3 }}
    >
      <CardContent isMain={isMain}>
        {ageGroup && (
          <TagContainer>
            {ageGroup}
          </TagContainer>
        )}
        <TitleSubtitleRow>
          <Title isMain={isMain}>{title}</Title>
          <Subtitle isMain={isMain}>{subtitle}</Subtitle>
        </TitleSubtitleRow>
        <ContentRow>
          {description && (
            <Description isMain={isMain} style={{ flex: 1 }}>
              {description}
            </Description>
          )}
          {hasImage && (
            <ImageContainer 
              layout={effectiveLayout}
              size={isMain ? '140px' : '90px'}
              moveUpPixels={moveUpPixels}
            >
              {image ? (
                <StyledImage src={image} alt={`${title} ${subtitle}`} />
              ) : (
                <div style={{ width: '100%', height: '100%', backgroundColor: '#FFA5D2' }} />
              )}
            </ImageContainer>
          )}
        </ContentRow>
        {hasButton && (
          <ArrowButton>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z" fill="black"/>
            </svg>
          </ArrowButton>
        )}
      </CardContent>
    </CardContainer>
  );
};

export default CourseCard; 